import { Button, Form, InputNumber, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FileIcon from "../../../assets/icons/FileIcon";
import PercentageIcon from "../../../assets/icons/PercentageIcon";
import { useGetEmployeeDetailsQuery } from "../../../features/employee/employeeApi";
import {
  useGetEmployeeBreakdownSalaryDetailsQuery,
  useUpdateSalaryDetailsMutation,
} from "../../../features/employeeSalarySetting/employeeSalarySettingApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { GetEmployeeBreakdownSalaryDetailsResponseType } from "../../../types/payroll";
import {
  calculatePercentage,
  percentageCalculate,
} from "../../../utils/number";
import InfoTitle from "../../common/InfoTitle";
import Loader from "../../ui/Loader";
import { replaceEditWithView } from "./utils";

type SalaryBreakdownType =
  GetEmployeeBreakdownSalaryDetailsResponseType["data"]["salary_info"]["breakdown_percentage"][0];

export default function SalaryInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { editId } = useParams();
  const { isError: isGetEmployeeDetailsError, error: getEmployeeDetailsError } =
    useGetEmployeeDetailsQuery(editId || "", {
      skip: !editId,
    });
  const {
    isLoading: isSalaryLoading,
    isError: isSalaryError,
    error: salaryError,
    data: salaryData,
  } = useGetEmployeeBreakdownSalaryDetailsQuery(editId || "", {
    skip: !editId,
  });
  const [updateSalaryDetails, { isLoading, isError, error, isSuccess }] =
    useUpdateSalaryDetailsMutation();
  useError(
    isGetEmployeeDetailsError,
    getEmployeeDetailsError,
    "Get employee data fail"
  );
  useError(isError, error, "Update salary setting fail");
  useError(isSalaryError, salaryError, "Get salary setting fail");

  const backLink = replaceEditWithView(location.pathname + location.search);
  const payrollSetting = salaryData?.data?.salary_info;
  const [salaryPercentage, setSalaryPercentage] = useState(0);
  const [salaryBreakdown, setSalaryBreakdown] =
    useState<SalaryBreakdownType[]>();

  const findCompanyData = (key: string) => {
    return payrollSetting?.global_gross_salary_component?.find(
      (item) => item?.name === key
    );
  };

  const findOverwrittenItem = (key: string, percentage: number) => {
    return Number(Number(findCompanyData(key)?.percentage) !== percentage);
  };

  const handleSalaryBreakdown = <K extends keyof SalaryBreakdownType>(
    id: number,
    key: K,
    value: SalaryBreakdownType[K]
  ) => {
    setSalaryBreakdown((prev) =>
      prev?.map((item) => {
        if (item?.id === id) {
          return {
            ...item,
            [key]: value,
            is_overwritten: findOverwrittenItem(
              item?.name,
              key === "percentage" ? Number(value) : Number(item?.percentage)
            ),
          };
        }

        return item;
      })
    );
  };

  const gross_salary = Number(Form.useWatch("gross_salary", form));

  useEffect(() => {
    const totalPercentage =
      salaryBreakdown?.reduce(
        (acc, percentage) => acc + percentage.percentage,
        0
      ) || 0;
    setSalaryPercentage(totalPercentage);
  }, [salaryBreakdown]);

  useEffect(() => {
    setSalaryBreakdown(
      payrollSetting?.breakdown_percentage?.length
        ? payrollSetting?.breakdown_percentage
            ?.filter((item) => item?.is_active)
            ?.map((item) => ({
              ...item,
              percentage: Number(item?.percentage),
            }))
        : payrollSetting?.global_gross_salary_component
            ?.filter((item) => item?.is_active)
            ?.map((item) => ({
              ...item,
              percentage: Number(item?.percentage),
            }))
    );
  }, [
    payrollSetting?.breakdown_percentage,
    payrollSetting?.global_gross_salary_component,
  ]);

  const onFinish = (values: any) => {
    const newData = {
      business_member_id: Number(editId),
      gross_salary: Number(values?.gross_salary),
      breakdown_percentage: salaryBreakdown?.map((item) => ({
        ...item,
        is_overwritten: item?.is_overwritten || 0,
      })),
    };

    updateSalaryDetails(newData);
  };

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Salary information updated successfully!");
      navigate(backLink);
    }
  }, [backLink, isSuccess, navigate]);

  return (
    <div>
      <InfoTitle className="mb-6" align="start" icon={<FileIcon />}>
        Salary Information
        <p className="text-body-text text-sm font-medium">
          Total sum of breakdown must be 100%
        </p>
        <p className="text-status-danger-text text-sm font-medium">
          {salaryPercentage !== 100 && "Gross Salary not equal 100%"}
        </p>
      </InfoTitle>

      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isSalaryLoading ? (
          <Loader />
        ) : (
          <>
            <div className="grid grid-cols-3 items-center gap-4 mb-4">
              <h6 className="text-header font-medium">Gross Salary</h6>
              <h6 className="text-status-danger-text text-xl font-semibold">
                {salaryPercentage || 0}%
              </h6>
              <Form.Item
                name="gross_salary"
                className="mb-0"
                rules={[
                  // {
                  //   required: true,
                  //   message: "This field is required",
                  // },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(
                          new Error("Enter valid gross salary")
                        );
                      }
                      if (value < 0) {
                        return Promise.reject(
                          new Error("Gross salary can't be negative")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                initialValue={payrollSetting?.gross_salary || undefined}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  prefix="BDT"
                  onChange={(val) =>
                    setSalaryBreakdown((prev) =>
                      prev?.map((item) => ({
                        ...item,
                        amount: percentageCalculate(
                          Number(val),
                          item?.percentage
                        ),
                      }))
                    )
                  }
                />
              </Form.Item>
            </div>
            {salaryBreakdown?.map((salary) => (
              <div
                className="grid grid-cols-3 items-center gap-4 mb-4"
                key={salary?.id}
              >
                <div>
                  <h6 className="text-header font-medium">{salary?.title}</h6>
                  {salary?.is_overwritten ? (
                    <div className="mt-0.5 space-x-1.5">
                      <span className="status overwritten">Overwritten</span>
                      <button
                        type="button"
                        className="text-accent text-sm"
                        onClick={() => {
                          handleSalaryBreakdown(
                            salary?.id,
                            "percentage",
                            Number(findCompanyData(salary?.name)?.percentage)
                          );
                          handleSalaryBreakdown(
                            salary?.id,
                            "amount",
                            percentageCalculate(
                              gross_salary,
                              Number(findCompanyData(salary?.name)?.percentage)
                            )
                          );
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  ) : null}
                </div>
                {/* <Form.Item name={salary?.key} className="mb-0"> */}
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  suffix={<PercentageIcon />}
                  disabled={!gross_salary}
                  value={salary?.percentage}
                  onChange={(val) => {
                    handleSalaryBreakdown(
                      salary?.id,
                      "percentage",
                      Number(val)
                    );
                    handleSalaryBreakdown(
                      salary?.id,
                      "amount",
                      percentageCalculate(gross_salary, Number(val))
                    );
                  }}
                />
                {/* </Form.Item> */}
                {/* <Form.Item name={salary?.key + "amount"} className="mb-0"> */}
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  prefix="BDT"
                  value={salary?.amount}
                  onChange={(val) => {
                    handleSalaryBreakdown(salary?.id, "amount", Number(val));

                    handleSalaryBreakdown(
                      salary?.id,
                      "percentage",
                      calculatePercentage(Number(val), gross_salary)
                    );
                  }}
                />
                {/* </Form.Item> */}
              </div>
            ))}
            {/* <div className="grid grid-cols-3 items-center gap-4 mb-4">
              <h6 className="text-header font-medium">House Rent</h6>
              <Form.Item
                name="houseRentPercentage"
                // label="House Rent"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  suffix={<PercentageIcon />}
                />
              </Form.Item>
              <Form.Item
                name="houseRentAmount"
                // label="Basic Salary"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  prefix="BDT"
                />
              </Form.Item>
            </div>
            <div className="grid grid-cols-3 items-center gap-4 mb-4">
              <h6 className="text-header font-medium">Medical Allowance</h6>
              <Form.Item
                name="medicalAllowancePercentage"
                // label="Medical Allowance"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  suffix={<PercentageIcon />}
                />
              </Form.Item>
              <Form.Item
                name="medicalAllowanceAmount"
                // label="Basic Salary"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  prefix="BDT"
                />
              </Form.Item>
            </div>
            <div className="grid grid-cols-3 items-center gap-4">
              <h6 className="text-header font-medium">Conveyance</h6>
              <Form.Item
                name="conveyancePercentage"
                // label="Conveyance"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  suffix={<PercentageIcon />}
                />
              </Form.Item>
              <Form.Item
                name="conveyanceAmount"
                // label="Basic Salary"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0"
                  prefix="BDT"
                />
              </Form.Item>
            </div> */}

            <Space className="w-full mt-6" size={12}>
              <Button
                className="normal-dark-button modal-button"
                onClick={() => navigate(backLink)}
                size="middle"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={isLoading}
                disabled={salaryPercentage !== 100}
              >
                Update
              </Button>
            </Space>
          </>
        )}
      </Form>
    </div>
  );
}
